import clsx from "clsx";
import { useWatch } from "react-hook-form";
import { UseFormReturn } from "react-hook-form";
import { format, subDays, addDays } from "date-fns";
import { useEffect, useState, useMemo, useRef } from "react";
import { AdjustmentsHorizontalIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { useStaffClinicListAPI } from "@/app/hooks/api_hooks/clinic/useStaffClinicListAPI";

interface IShiftSubService {
  sub_service_id: string;
  sub_service_name: string;
  duration: string;
}

interface IShiftService {
  service_id: string;
  service_name: string;
  sub_services: IShiftSubService[];
}

interface IShiftServiceType {
  id: string;
  name: string;
  services: IShiftService[];
}

interface IOrgServiceFlat {
  service_id: string;
  service_name: string;
  sub_services: IShiftSubService[];
  service_type_id?: string;
  service_type_name?: string;
}

interface ICityClinicFormFields {
  city_id: string;
  clinic_id: string;
  clinic_name: string;
  service_id: string[];
  subservice_id: string[];
}

interface IUnifiedFiltersProps {
  formHook: UseFormReturn<ICityClinicFormFields, any>;
  cities: any[];
  clinics: any[];
  services: IShiftServiceType[] | IOrgServiceFlat[];
  quickFilterCounts?: {
    today_count?: number;
    yesterday_count?: number;
    tomorrow_count?: number;
    day_after_tomorrow_count?: number;
    day_after_after_tomorrow_count?: number;
  } | null;
  onCityChange?: (cityId: string) => void;
  onClinicChange?: (clinicId: string) => void;
  onServiceChange?: (serviceIds: string[]) => void;
  onSubServiceChange?: (subServiceIds: string[]) => void;
  onDateRangeChange?: (range: string, isToday: boolean) => void;
  onCustomDateRangeChange?: (fromDate: string, toDate: string) => void;
  onApplyFilters?: () => void;
  resetClinicOnCityChange?: boolean;
  initialDateRange?: string;
}

const UnifiedFilters: React.FC<IUnifiedFiltersProps> = ({
  formHook,
  cities,
  clinics,
  services,
  quickFilterCounts,
  onCityChange,
  onClinicChange,
  onServiceChange,
  onSubServiceChange,
  onDateRangeChange,
  onCustomDateRangeChange,
  onApplyFilters,
  resetClinicOnCityChange = true,
  initialDateRange = format(new Date(), "yyyy-MM-dd"),
}) => {
  const fetchCities = useOrgCitiesListAPI("UI");
  const fetchClinics = useStaffClinicListAPI("UI");
  const isInitializedRef = useRef<boolean>(false);
  const today = useMemo(() => new Date(), []);
  const todayString = useMemo(() => format(today, "yyyy-MM-dd"), [today]);
  const [dateOptions, setDateOptions] = useState<{ label: string; date: Date; value: string }[]>([]);
  const [showMobileFilters, setShowMobileFilters] = useState<boolean>(false);
  const [selectedRange, setSelectedRange] = useState<string>(initialDateRange);
  const [selectedFromDate, setSelectedFromDate] = useState<string>("");
  const [selectedToDate, setSelectedToDate] = useState<string>("");
  const [selectionDate, setSelectionDate] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selectionDate",
  });
  const citySelectItems = useSelectMenuReducer(cities, "name", "id", {
    withAllSelectOption: true,
    allSelectItemOptions: {
      title: "All Cities",
    },
  });
  const selectedCityId = useWatch({
    control: formHook.control,
    name: "city_id",
  });
  const clinicSelectItemsRaw = useSelectMenuReducer(clinics, "display_name", "id", {
    withAllSelectOption: true,
    allSelectItemOptions: {
      title: "All Clinics",
    },
  });
  const clinicSelectItems = selectedCityId ? clinicSelectItemsRaw : [];

  const normalizedServiceTypes: IShiftServiceType[] = useMemo(() => {
    if (!services?.length) return [];
    const first = services[0] as any;
    if (first?.id && first?.services) {
      return services as IShiftServiceType[];
    }
    const grouped = new Map<string, IShiftServiceType>();
    (services as IOrgServiceFlat[]).forEach((service) => {
      const typeId = service.service_type_id || service.service_id;
      const typeName = service.service_type_name || service.service_name;
      if (!grouped.has(typeId)) {
        grouped.set(typeId, {
          id: typeId,
          name: typeName,
          services: [],
        });
      }
      grouped.get(typeId)!.services.push({
        service_id: service.service_id,
        service_name: service.service_name,
        sub_services: service.sub_services ?? [],
      });
    });
    return Array.from(grouped.values());
  }, [services]);

  const serviceSelectItems = useSelectMenuReducer(normalizedServiceTypes, "name", "id");
  const selectedServiceTypeIds = formHook.watch("service_id") || [];

  const selectedServicesForApi = useMemo(() => {
    if (!selectedServiceTypeIds.length) return [];
    const final: IShiftService[] = [];
    selectedServiceTypeIds.forEach((serviceTypeId) => {
      const selectedType = normalizedServiceTypes.find((s) => s.id === serviceTypeId);
      if (selectedType?.services?.length) {
        final.push(...selectedType.services);
      }
    });
    return final;
  }, [selectedServiceTypeIds, normalizedServiceTypes]);

  const availableSubServices = useMemo(() => {
    if (!selectedServicesForApi.length) return [];
    const subServices: IShiftSubService[] = [];
    selectedServicesForApi.forEach((service) => {
      if (service?.sub_services?.length) {
        subServices.push(...service.sub_services);
      }
    });
    return subServices;
  }, [selectedServicesForApi]);

  const subServiceSelectItems = useSelectMenuReducer(availableSubServices, "sub_service_name", "sub_service_id");

  useEffect(() => {
    const yesterday = subDays(today, 1);
    const tomorrow = addDays(today, 1);
    const dayAfterTomorrow = addDays(today, 2);
    const nextDay = addDays(today, 3);
    setDateOptions([
      {
        label: "Yesterday",
        date: yesterday,
        value: format(yesterday, "yyyy-MM-dd"),
      },
      {
        label: "Today",
        date: today,
        value: format(today, "yyyy-MM-dd"),
      },
      {
        label: "Tomorrow",
        date: tomorrow,
        value: format(tomorrow, "yyyy-MM-dd"),
      },
      {
        label: format(dayAfterTomorrow, "EEE d MMM"),
        date: dayAfterTomorrow,
        value: format(dayAfterTomorrow, "yyyy-MM-dd"),
      },
      {
        label: format(nextDay, "EEE d MMM"),
        date: nextDay,
        value: format(nextDay, "yyyy-MM-dd"),
      },
    ]);
  }, [today]);

  useEffect(() => {
    fetchCities({
      withClinics: false,
    });
  }, []);

  useEffect(() => {
    const cityId = formHook.getValues("city_id");
    if (cityId && !isInitializedRef.current) {
      if (cityId === "-1") {
        const allCityIds = cities.map((city) => city.id).filter((id) => id !== "-1");
        if (allCityIds.length > 0) {
          fetchClinics({ cityIds: allCityIds, withProviders: false });
        }
      } else {
        fetchClinics({ cityIds: [cityId], withProviders: false });
      }
      isInitializedRef.current = true;
    }
  }, [formHook.getValues("city_id"), cities]);

  useEffect(() => {
    const subscription = formHook.watch((value, { name }) => {
      if (name === "city_id") {
        const cityId = value.city_id;
        if (!cityId) {
          formHook.setValue("clinic_id", "");
          formHook.setValue("clinic_name", "");
          return;
        }
        if (cityId === "-1") {
          const allCityIds = cities.map((city) => city.id).filter((id) => id !== "-1");
          if (allCityIds.length > 0) {
            fetchClinics({ cityIds: allCityIds, withProviders: false });
          }
        } else {
          fetchClinics({ cityIds: [cityId], withProviders: false });
        }
        if (resetClinicOnCityChange) {
          formHook.setValue("clinic_id", "");
          formHook.setValue("clinic_name", "");
        }
        onCityChange?.(cityId);
      }
    });
    return () => subscription.unsubscribe();
  }, [cities]);

  useEffect(() => {
    if (selectedRange && !selectedFromDate && !selectedToDate) {
      const isToday = selectedRange === todayString;
      onDateRangeChange?.(selectedRange, isToday);
    }
  }, [selectedRange, todayString]);

  useEffect(() => {
    if (selectedFromDate && selectedToDate) {
      onCustomDateRangeChange?.(selectedFromDate, selectedToDate);
    }
  }, [selectedFromDate, selectedToDate]);

  useEffect(() => {
    const currentSubServices = formHook.watch("subservice_id") || [];
    if (currentSubServices.length > 0) {
      const validSubServices = currentSubServices.filter((subServiceId) =>
        availableSubServices.some((sub) => sub.sub_service_id === subServiceId),
      );
      if (validSubServices.length !== currentSubServices.length) {
        formHook.setValue("subservice_id", validSubServices);
      }
    }
  }, [selectedServiceTypeIds]);

  const handleQuickFilterClick = (value: string) => {
    const selectedDate = new Date(value);
    if (isNaN(selectedDate.getTime())) {
      console.warn("Invalid quick filter date:", value);
      return;
    }
    setSelectedRange(value);
    setSelectedFromDate("");
    setSelectedToDate("");
    setSelectionDate({
      startDate: selectedDate,
      endDate: selectedDate,
      key: "selectionDate",
    });
  };

  const getQuickFilterCount = (index: number) => {
    if (!quickFilterCounts) return 0;
    switch (index) {
      case 0:
        return quickFilterCounts.yesterday_count ?? 0;
      case 1:
        return quickFilterCounts.today_count ?? 0;
      case 2:
        return quickFilterCounts.tomorrow_count ?? 0;
      case 3:
        return quickFilterCounts.day_after_tomorrow_count ?? 0;
      case 4:
        return quickFilterCounts.day_after_after_tomorrow_count ?? 0;
      default:
        return 0;
    }
  };

  const handleDateRangeClick = () => {
    if (!selectionDate.startDate || !selectionDate.endDate) {
      console.warn("Invalid date selection - missing start or end date");
      return;
    }
    const startDate =
      selectionDate.startDate instanceof Date && !isNaN(selectionDate.startDate.getTime())
        ? selectionDate.startDate
        : new Date();
    const endDate =
      selectionDate.endDate instanceof Date && !isNaN(selectionDate.endDate.getTime())
        ? selectionDate.endDate
        : new Date();
    if (startDate > endDate) {
      console.warn("Invalid date range - start date is after end date");
      return;
    }
    try {
      const formattedStartDate = format(startDate, "yyyy-MM-dd");
      const formattedEndDate = format(endDate, "yyyy-MM-dd");
      setSelectedFromDate(formattedStartDate);
      setSelectedToDate(formattedEndDate);
      setSelectedRange("");
    } catch (error) {
      console.error("Error formatting dates:", error);
    }
  };

  const formatDisplayDate = (date: Date) => {
    return format(date, "dd MMM yyyy");
  };

  return (
    <>
      <div className="bg-white p-3 m-2 rounded-xl border border-[#EDEDED] shadow-sm">
        <div className="flex flex-col gap-3 w-full mb-3 md:mb-4 md:flex-row md:items-center md:gap-3">
          <div className="flex items-center justify-between gap-2 md:shrink-0">
            <h2 className="text-left font-semibold text-sm md:text-base text-[#1F1F23]">Shift Appointments</h2>
            <button
              type="button"
              aria-expanded={showMobileFilters}
              onClick={() => setShowMobileFilters((prev) => !prev)}
              className="inline-flex md:hidden items-center gap-1.5 h-8 px-3 rounded-full border border-[#D7D7D7] text-xs text-[#1F1F23] bg-white"
            >
              <AdjustmentsHorizontalIcon className="w-4 h-4" />
              <span>Filters</span>
              <ChevronDownIcon className={clsx("w-3.5 h-3.5 transition-transform", showMobileFilters && "rotate-180")} />
            </button>
          </div>
          <div className={clsx("w-full min-w-0 md:flex-1 md:block", showMobileFilters ? "block" : "hidden")}>
            <div className="scroll-inner flex items-center justify-start md:justify-end md:flex-wrap gap-2 max-w-full overflow-x-auto pb-1 md:pb-0 md:overflow-visible">
              {dateOptions.map((item, index) => {
                const isSelected = selectedRange === item.value;
                const count = getQuickFilterCount(index);
                return (
                  <button
                    key={`${item.value}-${index}`}
                    type="button"
                    onClick={() => handleQuickFilterClick(item.value)}
                    className={`inline-flex shrink-0 h-8 px-2.5 rounded-full text-xs whitespace-nowrap items-center justify-center gap-1.5 transition-all ${
                      isSelected
                        ? " text-[#000000] border-[#000000] border-[1px] bg-white"
                        : " border-[1px] bg-white text-[#6C727F]  hover:bg-[#E8E8E8]"
                    }`}
                  >
                    <span className="block whitespace-nowrap leading-none">{item.label}</span>
                    <span
                      className={`block shrink-0 text-[10px] leading-none ${isSelected ? "text-[#000000]" : "text-[#6C727F]"}`}
                    >
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div
          className={clsx(
            "w-full gap-3 md:gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:[grid-template-columns:repeat(5,minmax(0,1fr))_auto] md:grid",
            showMobileFilters ? "grid" : "hidden",)} >
          <div className="min-w-0">
            <SearchableSelectMenu
              errorMessages={[{ message: "City is required", type: "required" }]}
              showDropdownIcon
              placeholder="Select City"
              inputContainerClassName="h-[36px]"
              className="!py-[6px] text-xs"
              fieldError={formHook.formState.errors.city_id}
              register={formHook.register("city_id", { required: false })}
              showTypedErrors
              selectItems={citySelectItems}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("city_id", item.id);
                }
              }}
              defaultSelected={citySelectItems?.find((c) => c.id === formHook.watch("city_id"))}
            />
          </div>
          <div className="min-w-0">
            <SearchableSelectMenu
              errorMessages={[{ message: "Clinic is required", type: "required" }]}
              showDropdownIcon
              listZIndex="z-50"
              placeholder="Select Clinic"
              inputContainerClassName="h-[36px]"
              className="!py-[6px] text-xs"
              fieldError={formHook.formState.errors.clinic_id}
              register={formHook.register("clinic_id", { required: false })}
              showTypedErrors
              selectItems={clinicSelectItems}
              onSelectItem={(item) => {
                if (item) {
                  formHook.setValue("clinic_id", item.id);
                  formHook.setValue("clinic_name", item.title);
                  onClinicChange?.(item.id);
                }
              }}
              defaultSelected={clinicSelectItems?.find((c) => c.id === formHook.watch("clinic_id"))}
            />
          </div>
          <div className="min-w-0">
            <SearchableMultiSelectMenu
              withSelectAll
              errorMessages={[{ message: "Service is required", type: "required" }]}
              fieldError={formHook.formState.errors.service_id}
              register={formHook.register("service_id", { required: false })}
              showTypedErrors={false}
              containerClassName="h-[36px]"
              className="!py-[7px] text-xs"
              placeholder="Select Service Type"
              onSelectItem={(items) => {
                const selectedServiceTypeIds = items.map((i) => i.id);
                formHook.setValue("service_id", selectedServiceTypeIds);
                const selectedServices = selectedServiceTypeIds.flatMap(
                  (serviceTypeId) => normalizedServiceTypes.find((s) => s.id === serviceTypeId)?.services ?? [],
                );
                onServiceChange?.(selectedServices.map((service) => service.service_id));
                formHook.setValue("subservice_id", []);
                onSubServiceChange?.([]);
              }}
              defaultSelected={serviceSelectItems.filter((s) => (formHook.watch("service_id") || []).includes(s.id))}
              selectItems={serviceSelectItems}
            />
          </div>
          <div className="min-w-0">
            <SearchableMultiSelectMenu
              withSelectAll
              errorMessages={[{ message: "Sub Service is required", type: "required" }]}
              fieldError={formHook.formState.errors.subservice_id}
              register={formHook.register("subservice_id", { required: false })}
              showTypedErrors={false}
              containerClassName="h-[36px]"
              className="!py-[7px] text-xs"
              placeholder="Select Sub Services"
              onSelectItem={(items) => {
                const subServiceIds = items.map((i) => i.id);
                formHook.setValue("subservice_id", subServiceIds);
                onSubServiceChange?.(subServiceIds);
                const allSelectedServiceIds = selectedServicesForApi.map((service) => service.service_id);
                if (!subServiceIds.length) {
                  onServiceChange?.(allSelectedServiceIds);
                  return;
                }
                const parentServiceIds = [
                  ...new Set(
                    selectedServicesForApi
                      .filter((service) =>
                        service.sub_services?.some((sub) => subServiceIds.includes(sub.sub_service_id)),
                      )
                      .map((service) => service.service_id),
                  ),
                ];
                onServiceChange?.(parentServiceIds);
              }}
              defaultSelected={subServiceSelectItems.filter((s) =>
                (formHook.watch("subservice_id") || []).includes(s.id),
              )}
              selectItems={subServiceSelectItems}
              disabled={selectedServiceTypeIds.length === 0}
            />
          </div>
          <div className="min-w-0">
            <OMCalendarComponent
              selectionDate={selectionDate}
              setSelectionDate={setSelectionDate}
              startDate={formatDisplayDate(selectionDate.startDate)}
              endDate={formatDisplayDate(selectionDate.endDate)}
              handelClick={handleDateRangeClick}
              containerClassName="w-full [&>button]:h-[36px] [&>button]:py-0 [&>button>span:nth-child(2)]:!py-0 [&>button>span:nth-child(2)]:truncate [&>button>span:nth-child(2)]:overflow-hidden [&>button>span:nth-child(2)]:whitespace-nowrap [&>button>span:nth-child(2)]:min-w-0"
              isFutureDateDisabled={false}
              isPastDateDisabled={false}
              isPaddingOnTop={false}
              isDynamicCalender={true}
            />
          </div>
          <div className="w-full sm:col-span-2 md:col-span-1 md:w-fit">
            <SolidButton
              size="XL"
              onButtonClick={onApplyFilters}
              className="!h-[36px] !w-full md:!w-auto !px-5 whitespace-nowrap !bg-black text-white !hover:bg-black !border-black text-xs rounded-lg font-medium"
            >
              Apply Filter
            </SolidButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default UnifiedFilters;
